import { Inject, Injectable } from '@angular/core';
import { CUSTOMERENVIRONMENT } from '@app/common/app-tokens';
import { applyLanguageFilteringSingle } from '@app/common/filtering-behavior';
import { PageQuery } from '@app/common/page-query';
import { Page } from '@app/models/page';
import { SearchService } from '@app/services/search.service';
import { Environment } from '@environments/interface.environment';
import { combineLatest, iif, Observable, of } from 'rxjs';
import { map, mergeMap } from 'rxjs/operators';
import { FeatureSettingsStoreService } from './feature-settings-store.service';
import { LicenseService } from './license.service';
import { PagesService } from './pages.service';
import { UserLanguageTermService } from './user-language-term.service';

@Injectable({
  providedIn: 'root'
})
export class PageCenterService {

  constructor(@Inject(CUSTOMERENVIRONMENT) private environment: Environment,
              private pagesService: PagesService,
              private searchService: SearchService,
              private featureSettingsStoreService: FeatureSettingsStoreService,
              private licenseService: LicenseService,
              private userLanguageTermService: UserLanguageTermService,
  ) { }

  /**
   * Returns all pages matching the query. Filtered by language if: language filtering is enabled/configured in the environment and the parameter overrideLanguageFilter is false
   * The returned result could depending on configuration and user licenses be sharepoint pages or workplace pages or a combination of both.
   * @param query the page query
   * @param overrideLanguageFilter override the language filter if true. This doesn't have any effect if the filtering is disabled to begin with.
   */
  public getAllPagesByQuery(query: PageQuery, overrideLanguageFilter: boolean): Observable<Array<Page>> {

    const pagesFromSharePoint$ = this.licenseService.hasUserAny365License().pipe(
      mergeMap(isLicensed =>
        iif(() => isLicensed,
          this.searchService.getModernPagesByPageQuery(query).pipe(
            map(searchResultFromSharePoint => {
              // console.log(searchResultFromSharePoint);
              return searchResultFromSharePoint.map(item =>
                Page.fromSearchResult(item,
                  this.environment.featureSettings.managedPropertyConfig.newsIsTopNewsManagedProperty,
                  this.environment.featureSettings.managedPropertyConfig.newsChannelManagedProperty,
                  this.environment.featureSettings.managedPropertyConfig.newsLanguageManagedProperty,
                  query.PageType,
                  this.environment.featureSettings.managedPropertyConfig.newsSenderTagsManagedProperty)
              );
            })
          ),
          of([]),
        )
      )
    );

    // Returns sharepoint pages matching the query. If language filtering is enabled and configured in the environment they will be filtered by user selected language as well.
    const languageFilteredPagesFromSharePoint$ = pagesFromSharePoint$
      .pipe(
        mergeMap((pages) => {
          if (this.featureSettingsStoreService.state.enableLanguageFilteringForSharepointPages && !overrideLanguageFilter) {
            return this.userLanguageTermService.getUserLanguageTerms()
              .pipe(
                map((userLanguageTerms) => {
                  return applyLanguageFilteringSingle(pages, userLanguageTerms, this.featureSettingsStoreService.state.enableShowNonLanguageContentForAllUsers, this.featureSettingsStoreService.state.defaultLanguageTermId);
                })
              );
          } else {
            return of(pages);
          }
        }));

    // Returns workplace pages matching the query. If language filtering is enabled and configured in the environment they will be filtered by user selected language as well.
    const languageFilteredPagesFromWorkplace$ = this.pagesService.getPagesByQuery(query)
      .pipe(
        mergeMap((pages) => {
          if (this.featureSettingsStoreService.state.enableLanguageFilteringForWorkplace && !overrideLanguageFilter) {
            return this.userLanguageTermService.getUserLanguageTerms()
              .pipe(
                map((userLanguageTerms) => {
                  return applyLanguageFilteringSingle(pages, userLanguageTerms, this.featureSettingsStoreService.state.enableShowNonLanguageContentForAllUsers, this.featureSettingsStoreService.state.defaultLanguageTermId);
                })
              );
          } else {
            return of(pages);
          }
        }));

    return iif(
      () => this.environment.enableWorkplaceLight,
      combineLatest([languageFilteredPagesFromWorkplace$, languageFilteredPagesFromSharePoint$])
        .pipe(
          map(([pagesFromApi, pagesFromSharePoint]) => {
            // return workplace light and sharepoint pages
            // note that for unlicensed users (light users) the sharepoint pages is a empty array due to a earlier check
            return pagesFromApi.concat(pagesFromSharePoint);
          })
        ),
      // if workplace light isn't enabled then return only sharepoint pages
      languageFilteredPagesFromSharePoint$);
  }
}
